/** Données contact + réseaux sociaux pour le HTML SEO statique (fallback si API indisponible). */
export type SocialLink = {
  key: string;
  label: string;
  url: string;
};

export type SeoContactSettings = {
  phone: string;
  email: string;
  address: string;
  workingHours: string;
  googleMaps: string;
  googleReview: string;
  socialLinks: SocialLink[];
  sameAs: string[];
};

export const SEO_CONTACT_FALLBACK: SeoContactSettings = {
  phone: '+216 71 000 000',
  email: 'contact@lbennaproduction.tn',
  address: 'Cyberpark, El Ghazala, Tunis',
  workingHours: 'Lun-Sam 9h-19h',
  googleMaps: '',
  googleReview: '',
  socialLinks: [],
  sameAs: [],
};

const SOCIAL_FIELDS: { key: string; label: string }[] = [
  { key: 'social_facebook', label: 'Facebook' },
  { key: 'social_instagram', label: 'Instagram' },
  { key: 'social_youtube', label: 'YouTube' },
  { key: 'social_tiktok', label: 'TikTok' },
  { key: 'social_linkedin', label: 'LinkedIn' },
];

/** Ignore les URLs placeholder type https://facebook.com ou https://linkedin.com seuls. */
export function isUsefulSocialUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed.startsWith('http')) return false;
  try {
    const { hostname, pathname, search } = new URL(trimmed);
    const host = hostname.replace(/^www\./, '');
    if ((host === 'facebook.com' || host === 'linkedin.com' || host === 'youtube.com') && pathname === '/' && !search) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function buildSeoContactFromApi(data: Record<string, unknown>): SeoContactSettings {
  const socialLinks: SocialLink[] = SOCIAL_FIELDS.map(({ key, label }) => ({
    key,
    label,
    url: String(data[key] ?? '').trim(),
  })).filter((item) => isUsefulSocialUrl(item.url));

  const googleMaps = String(data.social_google_maps ?? '').trim();
  const googleReview = String(data.social_google_review ?? '').trim();

  const sameAs = [
    ...socialLinks.map((s) => s.url),
    ...(isUsefulSocialUrl(googleMaps) ? [googleMaps] : []),
    ...(isUsefulSocialUrl(googleReview) ? [googleReview] : []),
  ];

  return {
    phone: String(data.contact_phone ?? SEO_CONTACT_FALLBACK.phone).trim() || SEO_CONTACT_FALLBACK.phone,
    email: String(data.contact_email ?? SEO_CONTACT_FALLBACK.email).trim() || SEO_CONTACT_FALLBACK.email,
    address: String(data.contact_address ?? SEO_CONTACT_FALLBACK.address).trim() || SEO_CONTACT_FALLBACK.address,
    workingHours:
      String(data.contact_working_hours ?? SEO_CONTACT_FALLBACK.workingHours).trim() ||
      SEO_CONTACT_FALLBACK.workingHours,
    googleMaps: isUsefulSocialUrl(googleMaps) ? googleMaps : '',
    googleReview: isUsefulSocialUrl(googleReview) ? googleReview : '',
    socialLinks,
    sameAs: [...new Set(sameAs)],
  };
}
